import { v4 as uuidv4 } from "uuid";
import { SHIPPING_METHODS, DIRECT_SIGNATURE_CHARGE_SGD } from "@/lib/constants";
import { roundNumber } from "@/utils/number";
import { stringSimilarity } from "@/utils/text";

export const generateEmptyFormData = () => ({
  packages: [
    {
      key: uuidv4(),
      length: "",
      width: "",
      height: "",
      weight: "",
      boxName: "",
      regularPackingCost: 0,
      fragilePackingCost: 0,
      validPackingType: true,
      validLength: "none",
      validWidth: "none",
      validHeight: "none",
      validWeight: "none",
      packingType: "No",
      items: [
        {
          key: uuidv4(),
          isFragile: false,
          category: "",
          quantity: 1,
          description: "",
          unitWeight: "",
          totalWeight: "",
          unitValue: "",
          totalValue: "",
          validQuantity: "none",
          validDescription: "none",
          validCategory: "none",
          validTotalValue: "none",
          validTotalWeight: "none",
        },
      ],
    },
  ],

  // Metadata
  key: uuidv4(),
  createdAt: "",
  purpose: "",

  // Recipient
  recipientName: "",
  recipientEmail: "",
  recipientAddress: "",
  recipientPostalCode: "",
  recipientCity: "",
  recipientState: "",
  recipientCountry: "",
  recipientCallCode: "",
  recipientMobileNo: "",

  // Sender
  senderFirstName: "",
  senderLastName: "",
  senderEmail: "",
  senderCallCode: "SG/+65",
  senderMobileNo: "",
  contactPersonCallCode: "SG/+65",
  contactPersonMobileNo: "",

  // Delivery
  shippingMethod: SHIPPING_METHODS.EXPRESS.value,
  DOPU: "",
  pickupAddress: "",
  pickupPostalCode: "",
  pickupUnitNo: "",
  date: "",
  time: "",

  // Add-ons
  dutiesPaidBy: "RECIPIENT",
  taxes: 0,
  duties: 0,
  dutyHandlingFee: 0,
  isRural: false,
  ruralSurchargeAmount: 0,
  isInsured: false,
  insuranceAmount: 0,
  directSignature: false,
  signatureAmount: roundNumber(DIRECT_SIGNATURE_CHARGE_SGD),
  contactSender: true,
  countryChanged: false,

  // Costs
  totalPackingCost: 0,
  initialFreightQuote: 0,
  discountCode: "",
  initialDiscountAmount: 0,
  initialValentinesAmount: 0,
  customValue: 0,
  totalAmount: 0,
  preferentialRate: 0,
  chargeableWeight: 0,

  // E-commerce
  draftID: null,
  storeOrderId: null,
  storeOrderName: null,
  storeOrderCreatedAt: null,
});

/**
 * Check if the minimum required fields have values.
 */
export const isShipmentFormEmpty = (shipment) => {
  const {
    packages,
    recipientName,
    recipientEmail,
    recipientAddress,
    recipientCountry,
    recipientMobileNo,
  } = shipment;

  if (
    recipientName ||
    recipientEmail ||
    recipientAddress ||
    recipientCountry ||
    recipientMobileNo
  ) {
    return false;
  }

  const isValidDimension = (value) => {
    const num = Number(value);
    return !isNaN(num) && num > 0;
  };

  for (const pkg of packages) {
    const { items, length, width, height, weight } = pkg;
    if (
      isValidDimension(length) ||
      isValidDimension(width) ||
      isValidDimension(height) ||
      isValidDimension(weight)
    ) {
      return false;
    }
    if (items.some((item) => !!item.description)) return false;
  }

  return true;
};

export const generateShipmentDescription = (shipment, { maxLength = 30 }) => {
  // Use the item category to generate a generic shipment description:
  const itemCategories = [];
  for (const pkg of shipment.packages) {
    for (const item of pkg.items) {
      itemCategories.push(item.category);
    }
  }

  // 1. Create a frequency map (string -> count).
  const frequencyMap = {};
  for (const str of itemCategories) {
    frequencyMap[str] = (frequencyMap[str] || 0) + 1;
  }

  // 2. Get the unique strings (keys of the frequency map).
  // 3. Sort them by their frequencies in descending order.
  const sortedUniqueStrings = Object.keys(frequencyMap).sort(
    (a, b) => frequencyMap[b] - frequencyMap[a]
  );

  // 4. Return the first unique strings, until the total length is <= maxLength.
  let shipmentDescription = "";
  for (const item of sortedUniqueStrings) {
    const formattedItem = shipmentDescription.length === 0 ? item : `, ${item}`;
    if (shipmentDescription.length + formattedItem.length > maxLength) break;

    shipmentDescription += formattedItem;
  }

  return shipmentDescription;
};

const findSimilarItemIndex = (item, items) => {
  const itemDescription = item.description.toLowerCase();

  for (let index = 0; index < items.length; index++) {
    const existingItem = items[index];
    const curSimiliarity = stringSimilarity(
      itemDescription,
      existingItem.description
    );

    if (curSimiliarity >= 0.7) {
      return index;
    }
  }

  return -1;
};

export const groupItemsByDescription = (items) => {
  let groupedItems = [];

  for (const item of items) {
    const similarItemIndex = findSimilarItemIndex(item, groupedItems);

    // No similar item found
    if (similarItemIndex === -1) {
      groupedItems.push(item);
      continue;
    }

    const similarItem = groupedItems[similarItemIndex];
    groupedItems[similarItemIndex] = {
      ...similarItem,

      quantity: similarItem.quantity + item.quantity,
      totalValue: similarItem.totalValue + item.quantity * item.unitValue,
      totalWeight: similarItem.totalWeight + item.quantity * item.unitWeight,
    };
  }

  return groupedItems;
};
